<template>
  <div>
    <v-progress-linear v-if="loadingDeposito" indeterminate class="mb-2" />
    <v-alert v-else-if="!depositoIdSeleccionado" type="warning" dense class="mb-3">
      Seleccioná un depósito en la configuración del usuario para ver las órdenes de venta.
    </v-alert>
    <v-row v-else no-gutters>
      <v-col cols="12" md="3" lg="2">
        <orden-venta-filters
          @updateFilters="setFilters"
          @clearFilter="clearFilters"
        />
      </v-col>
      <v-col cols="12" md="9" lg="10" class="pl-md-3">
    <v-dialog
      v-model="editTableDialogOpen"
      max-width="380"
      persistent
      content-class="edit-table-dialog"
    >
      <v-card class="edit-table-card">
        <div class="edit-table-header">
          <span class="edit-table-title">Columnas visibles</span>
          <v-btn icon dark class="edit-table-close" @click="editTableDialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-list class="edit-table-list">
          <v-list-item
            v-for="col in toggleableColumns"
            :key="col.value"
            class="edit-table-item"
          >
            <v-list-item-content>
              <v-list-item-title>{{ col.text }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="mr-0">
              <v-btn icon small @click="toggleColumnVisibility(col.value)">
                <v-icon :color="visibleColumnValues.includes(col.value) ? 'primary' : 'grey'">
                  {{ visibleColumnValues.includes(col.value) ? 'mdi-eye' : 'mdi-eye-off' }}
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>

    <!-- Actions Toolbar (slot reservado para que la tabla no se mueva al mostrar/ocultar) -->
    <div class="toolbar-slot">
      <v-slide-y-transition>
        <v-toolbar v-if="selected.length > 0" dense flat class="mb-1 actions-toolbar" color="grey lighten-4" height="42">
          <v-btn icon small color="grey darken-2" @click="clearSelection">
            <v-icon>mdi-close</v-icon>
          </v-btn>

          <span class="subtitle-2 font-weight-medium ml-2 mr-3 actions-toolbar-text">
            {{ selected.length }} seleccionada{{ selected.length !== 1 ? 's' : '' }}
          </span>

          <v-tooltip bottom nudge-bottom="2" content-class="custom-tooltip" close-delay="0">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="grey darken-2"
                v-bind="attrs"
                v-on="on"
                @click="facturarSeleccionadas"
                class="mr-1"
              >
                <v-icon>mdi-note-plus</v-icon>
              </v-btn>
            </template>
            <span>Facturar seleccionadas</span>
          </v-tooltip>
        </v-toolbar>
      </v-slide-y-transition>
    </div>

    <div class="table-fixed-header-wrap">
    <v-data-table
      v-model="selected"
      show-select
      item-key="id"
      fixed-header
      height="calc(100vh - 260px)"
      :headers="visibleHeaders"
      :items="items"
      :server-items-length="totalItems"
      :loading="loading"
      :options.sync="options"
      :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50] }"
      @update:options="fetch"
    >
      <template #item.numeroOrdenVenta="{ item, value }">
        <span class="font-weight-medium">{{ textoNumeroOrdenVenta(item, value) }}</span>
      </template>
      <template #item.cliente="{ item }">
        <span
          class="cliente-cell"
          :title="item.cliente ? item.cliente.razonSocial : ''"
        >{{ item.cliente ? item.cliente.razonSocial : "—" }}</span>
      </template>
      <template #item.vendedor="{ item }">
        {{ item.vendedor ? item.vendedor.nombre + ' ' + item.vendedor.apellido : "—" }}
      </template>
      <template #item.precioTotal="{ item }">
        ${{ Number(item.precioTotal || 0).toFixed(2) }}
      </template>
      <template #item.createdAt="{ item }">
        {{ formatFecha(item.createdAt) }}
      </template>
      <template #item.estado="{ item }">
        <v-chip small dark class="white--text" :color="estadoColor(item.estado)">{{ labelEstadoOrdenVenta(item.estado) }}</v-chip>
      </template>
      <template #item.estadoPago="{ item }">
        <v-chip small dark class="white--text" :color="item.estadoPago === 'PAGADO' ? 'success' : 'error'">
          {{ labelEstadoPago(item.estadoPago) }}
        </v-chip>
      </template>
      <template #item.estadoFacturacion="{ item }">
        <v-chip small dark class="white--text" :color="estadoFacturacionColor(item.estadoFacturacion)">
          {{ labelEstadoFacturacion(item.estadoFacturacion) }}
        </v-chip>
      </template>
      <template #item.fechaPagoCompletado="{ item }">
        {{ item.fechaPagoCompletado ? formatFecha(item.fechaPagoCompletado) : "—" }}
      </template>
      <template #item.montoComisionVendedor="{ item }">
        {{ formatMontoComisionVendedor(item.montoComisionVendedor) }}
      </template>
      <template #item.actions="{ item }">
        <div class="table-actions-cell">
          <show-button icon="mdi-eye" @click="$emit('show', item)" />
          <edit-button v-if="ordenEditable(item) && !getFacturaDeOrden(item)" @click="$emit('edit', item)" />
          <v-tooltip
            v-if="puedeDescargarRemito(item)"
            bottom
            nudge-bottom="2"
            content-class="custom-tooltip"
            open-delay="200"
            close-delay="0"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" @click="descargarRemitoPDF(item)">
                <v-icon small color="blue">mdi-truck-delivery</v-icon>
              </v-btn>
            </template>
            <span>Descargar remito PDF</span>
          </v-tooltip>
          <v-tooltip
            v-if="puedeDescargarFactura(item)"
            bottom
            nudge-bottom="2"
            content-class="custom-tooltip"
            open-delay="200"
            close-delay="0"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" @click="descargarFacturaPDF(item)">
                <v-icon small color="red">mdi-file-pdf-box</v-icon>
              </v-btn>
            </template>
            <span>Descargar factura PDF</span>
          </v-tooltip>
        </div>
      </template>
  </v-data-table>
    </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { EditButton, ShowButton } from "@dracul/common-frontend";
import OrdenVentaProvider from "../../providers/OrdenVentaProvider";
import FacturacionProvider from "../../../facturacion/providers/FacturacionProvider";
import UserConfigurationProvider from "../../../user-configuration/providers/UserConfigurationProvider";
import { formatFechaVentas } from "../../utils/dateFormat";
import { labelEstadoOrdenVenta, labelEstadoPago } from "../../utils/estadoLabels";
import OrdenVentaFilters from "./OrdenVentaFilters/OrdenVentaFilters.vue";

export default {
  name: "OrdenVentaList",
  components: { EditButton, ShowButton, OrdenVentaFilters },
  data() {
    return {
      items: [],
      totalItems: 0,
      loading: false,
      loadingDeposito: false,
      downloadingFacturaId: null,
      downloadingRemitoId: null,
      depositoIdSeleccionado: null,
      facturasMap: {},
      activeFilters: {
        estado: null,
        estadoPago: null,
        estadoFacturacion: null,
        clienteNombre: null,
        vendedorNombre: null,
        fechaDesde: null,
        fechaHasta: null,
        fechaPagoCompletadoDesde: null,
        fechaPagoCompletadoHasta: null,
        precioTotalMin: null,
        precioTotalMax: null,
        descuentoMin: null,
        recargoMin: null,
      },
      visibleColumnValues: [],
      editTableDialogOpen: false,
      selected: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["createdAt"],
        sortDesc: [true],
      },
    };
  },
  created() {
    this.initVisibleColumns();
  },
  mounted() {
    this.loadDepositoSeleccionado();
  },
  activated() {
    this.loadDepositoSeleccionado();
  },
  computed: {
    allHeaders() {
      return [
        { text: "Nº OV", value: "numeroOrdenVenta", width: 88, sortable: true },
        { text: "Cliente", value: "cliente", width: 400, cellClass: "cell-cliente" },
        { text: "Vendedor", value: "vendedor", sortable: false },
        { text: "Total", value: "precioTotal" },
        { text: "Estado", value: "estado" },
        { text: "Pago", value: "estadoPago" },
        { text: "Facturación", value: "estadoFacturacion", sortable: false },
        { text: "Fecha de creación", value: "createdAt" },
        { text: "Fecha pago total", value: "fechaPagoCompletado", sortable: false },
        { text: "Comisión vendedor", value: "montoComisionVendedor", sortable: false },
        {
          text: "Acciones",
          value: "actions",
          sortable: false,
          width: 188,
          cellClass: "cell-actions-4",
          class: "cell-actions-4",
        },
      ];
    },
    toggleableColumns() {
      return this.allHeaders.filter((h) => h.value !== "actions");
    },
    visibleHeaders() {
      return this.allHeaders.filter(
        (h) => h.value === "actions" || this.visibleColumnValues.includes(h.value)
      );
    },
  },
  watch: {
    visibleColumnValues() {
      this.validateOrderByForVisibleColumns();
    },
  },
  methods: {
    openEditTableDialog() {
      this.editTableDialogOpen = true;
    },
    initVisibleColumns() {
      const STORAGE_KEY = "ordenVentaListVisibleColumns";
      const validValues = this.toggleableColumns.map((c) => c.value);
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          const filtered = [...new Set(parsed)].filter((v) => validValues.includes(v));
          if (filtered.length > 0) {
            this.visibleColumnValues = filtered;
            return;
          }
        }
      } catch (e) {
        // ignore invalid stored data
      }
      this.visibleColumnValues = [...validValues];
      this.saveVisibleColumnsToStorage();
    },
    saveVisibleColumnsToStorage() {
      try {
        localStorage.setItem(
          "ordenVentaListVisibleColumns",
          JSON.stringify(this.visibleColumnValues)
        );
      } catch (e) {
        // ignore
      }
    },
    toggleColumnVisibility(value) {
      const isVisible = this.visibleColumnValues.includes(value);
      if (isVisible && this.visibleColumnValues.length <= 1) return;
      if (isVisible) {
        this.visibleColumnValues = this.visibleColumnValues.filter((v) => v !== value);
      } else {
        this.visibleColumnValues = [...this.visibleColumnValues, value];
      }
      this.saveVisibleColumnsToStorage();
    },
    validateOrderByForVisibleColumns() {
      const sortBy = this.options.sortBy && this.options.sortBy[0];
      if (sortBy && sortBy !== "actions" && !this.visibleColumnValues.includes(sortBy)) {
        const firstVisible = this.visibleColumnValues[0] || "createdAt";
        this.options = {
          ...this.options,
          sortBy: [firstVisible],
        };
        if (this.depositoIdSeleccionado) this.fetch();
      }
    },
    textoNumeroOrdenVenta(item, valueFromSlot) {
      const raw = valueFromSlot !== undefined && valueFromSlot !== null ? valueFromSlot : item && item.numeroOrdenVenta;
      if (raw === null || raw === undefined || raw === "") return "—";
      const n = Number(raw);
      return Number.isFinite(n) ? String(Math.trunc(n)) : "—";
    },
    formatFecha(val) {
      return formatFechaVentas(val);
    },
    formatMontoComisionVendedor(val) {
      if (val == null || val === "") return "—";
      const n = Number(val);
      return Number.isFinite(n) ? `$${n.toFixed(2)}` : "—";
    },
    labelEstadoOrdenVenta,
    labelEstadoPago,
    labelEstadoFacturacion(estado) {
      const m = {
        SIN_FACTURA: "Sin factura",
        FACTURADA: "Facturada",
        ERROR_FACTURACION: "Error",
      };
      return m[estado] || "Sin factura";
    },
    estadoFacturacionColor(estado) {
      if (estado === "FACTURADA") return "success";
      if (estado === "ERROR_FACTURACION") return "error";
      return "grey";
    },
    facturarSeleccionadas() {
      const ids = (this.selected || []).map((i) => i.id).filter(Boolean);
      if (!ids.length) return;
      this.$emit("facturar-masiva", ids);
    },
    clearSelection() {
      this.selected = [];
    },
    estadoColor(estado) {
      const m = { CONFIRMADO: "blue", ENVIADO: "orange", RECIBIDO: "green", CANCELADO: "red" };
      return m[estado] || "grey";
    },
    getFacturaDeOrden(item) {
      if (item && item.facturaId) return { id: item.facturaId };
      const movimientoId = item && item.movimientoId;
      if (!movimientoId) return null;
      return this.facturasMap[movimientoId] || null;
    },
    puedeDescargarFactura(item) {
      return !!this.getFacturaDeOrden(item);
    },
    ordenEditable(item) {
      // Permitir modificar mientras la orden no haya sido enviada/recibida/cancelada.
      return item && item.estado === "CONFIRMADO";
    },
    puedeDescargarRemito(item) {
      return item && (item.estado === "ENVIADO" || item.estado === "RECIBIDO");
    },
    nombreArchivoRemito(item) {
      const nro = item && item.numeroOrdenVenta != null ? item.numeroOrdenVenta : item && item.id;
      const cliente = item && item.cliente && item.cliente.razonSocial
        ? String(item.cliente.razonSocial).replace(/[/\\:*?"<>|]/g, "").trim().slice(0, 40)
        : "cliente";
      const fechaStr = item && item.createdAt
        ? formatFechaVentas(item.createdAt).slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      const partes = ["Remito_OV", nro ? `N${nro}` : "", cliente, fechaStr].filter(Boolean);
      return `${partes.join("_")}.pdf`;
    },
    nombreArchivoFactura(item, factura) {
      const numero = factura && factura.numeroComprobante != null ? factura.numeroComprobante : "";
      const cliente = item && item.cliente && item.cliente.razonSocial
        ? String(item.cliente.razonSocial).replace(/[/\\:*?"<>|]/g, "").trim().slice(0, 40)
        : "cliente";
      const fechaStr = item && item.createdAt
        ? formatFechaVentas(item.createdAt).slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      const partes = ["Factura_OV", numero ? `N${numero}` : "", cliente, fechaStr].filter(Boolean);
      return `${partes.join("_")}.pdf`;
    },
    async cargarFacturasPorMovimientos(items) {
      this.facturasMap = {};
      const movimientoIds = (items || [])
        .map((it) => it && it.movimientoId)
        .filter((id) => !!id);
      if (!movimientoIds.length) return;
      try {
        const r = await FacturacionProvider.getFacturasExistenPorMovimientos(movimientoIds);
        const facturas = (r.data && r.data.facturasExistenPorMovimientos) || [];
        const map = {};
        facturas.forEach((f) => {
          if (f && f.movimientoId) map[f.movimientoId] = f;
        });
        this.facturasMap = map;
      } catch (e) {
        console.error(e);
      }
    },
    async descargarFacturaPDF(item) {
      const factura = this.getFacturaDeOrden(item);
      const facturaId = factura && factura.id;
      if (!facturaId || this.downloadingFacturaId) return;
      this.downloadingFacturaId = facturaId;
      try {
        const r = await FacturacionProvider.generarPDFBase64(facturaId);
        const pdfResult = r.data && r.data.facturaGenerarPDFBase64;
        const base64 = pdfResult && pdfResult.base64;
        const filename = (pdfResult && pdfResult.filename) || this.nombreArchivoFactura(item, factura);
        if (!base64) return;
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.error(e);
      } finally {
        this.downloadingFacturaId = null;
      }
    },
    async descargarRemitoPDF(item) {
      const ordenId = item && item.id;
      if (!ordenId || this.downloadingRemitoId) return;
      this.downloadingRemitoId = ordenId;
      try {
        const r = await OrdenVentaProvider.remitoGenerarPDFBase64(ordenId);
        const base64 = r.data && r.data.ordenVentaRemitoGenerarPDFBase64;
        if (!base64) return;
        const filename = this.nombreArchivoRemito(item);
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.error(e);
      } finally {
        this.downloadingRemitoId = null;
      }
    },
    async loadDepositoSeleccionado() {
      this.loadingDeposito = true;
      try {
        let depositoId = UserConfigurationProvider.getDepositoIdFromCache();
        if (!depositoId) {
          const r = await UserConfigurationProvider.getUserConfiguration(true);
          const config = r.data && r.data.userConfigurationGet;
          depositoId = (config && config.depositoIdSeleccionado) || (config && config.deposito && config.deposito.id) || null;
        }
        this.depositoIdSeleccionado = depositoId;
        if (depositoId) this.fetch();
        else {
          this.items = [];
          this.totalItems = 0;
          this.facturasMap = {};
        }
      } catch (e) {
        console.error(e);
        this.depositoIdSeleccionado = null;
        this.items = [];
        this.totalItems = 0;
        this.facturasMap = {};
      } finally {
        this.loadingDeposito = false;
      }
    },
    setFilters(filters) {
      this.options.page = 1;
      this.activeFilters = { ...filters };
      this.fetch();
    },
    clearFilters() {
      this.options.page = 1;
      this.activeFilters = {
        estado: null,
        estadoPago: null,
        clienteNombre: null,
        vendedorNombre: null,
        fechaDesde: null,
        fechaHasta: null,
        fechaPagoCompletadoDesde: null,
        fechaPagoCompletadoHasta: null,
        precioTotalMin: null,
        precioTotalMax: null,
        descuentoMin: null,
        recargoMin: null,
      };
      this.fetch();
    },
    fetch() {
      if (!this.depositoIdSeleccionado) {
        this.items = [];
        this.totalItems = 0;
        this.facturasMap = {};
        return;
      }
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const orderBy = sortBy && sortBy[0] ? sortBy[0] : "createdAt";
      const orderDesc = sortDesc && sortDesc[0] !== undefined ? sortDesc[0] : true;
      const filters = { depositoId: this.depositoIdSeleccionado, ...this.activeFilters };
      OrdenVentaProvider.paginateOrdenesVenta(page, itemsPerPage, null, orderBy, orderDesc, filters)
        .then(async (r) => {
          this.items = (r.data && r.data.ordenVentaPaginate && r.data.ordenVentaPaginate.items) || [];
          this.totalItems = (r.data && r.data.ordenVentaPaginate && r.data.ordenVentaPaginate.totalItems) || 0;
          await this.cargarFacturasPorMovimientos(this.items);
        })
        .catch((err) => console.error(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped>
.table-fixed-header-wrap {
  min-height: 0;
}

.cliente-cell {
  display: block;
  max-width: 500px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Reserva espacio fijo para la toolbar de acciones (42px toolbar + 4px mb-1) */
.toolbar-slot {
  min-height: 46px;
}

.actions-toolbar {
  border-radius: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
}

/* Gris oscuro solo para íconos de esta barra (sobreescribe estilos globales del toolbar) */
::v-deep .actions-toolbar.v-toolbar .v-btn .v-icon,
::v-deep .actions-toolbar.v-toolbar .v-btn--icon .v-icon {
  color: #424242 !important;
}

::v-deep .actions-toolbar span {
  color: #424242 !important;
}

::v-deep .cell-cliente {
  max-width: 500px;
}
</style>

<style>
.custom-tooltip {
  background-color: #424242 !important;
  opacity: 1 !important;
}

.custom-tooltip span {
  color: white !important;
}
</style>

<style>
.edit-table-dialog .v-dialog {
  border-radius: 8px;
  overflow: hidden;
}

.edit-table-card {
  overflow: hidden;
}

.edit-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #1976d2;
  color: white;
  font-size: 1rem;
  font-weight: 600;
}

.edit-table-title {
  color: white;
}

.edit-table-close {
  color: white !important;
}

.edit-table-close .v-icon {
  color: white !important;
}

.edit-table-list {
  max-height: 400px;
  overflow-y: auto;
}

.edit-table-item {
  border-bottom: 1px solid #eee;
}

.edit-table-item:last-child {
  border-bottom: none;
}
</style>
